import React, { useState } from 'react';
import { BADGES, BADGE_CATEGORIES } from '../data/badges';
import '../styles/BadgeManagement.css';
import {
  FaSearch,
  FaFilter,
  FaPlus,
  FaEdit,
  FaTrash,
  FaStar,
  FaGlobe,
  FaAward,
  FaCrown,
  FaMedal,
  FaUserCheck,
  FaImage,
  FaCheckCircle,
  FaHandshake,
  FaHeart,
  FaUsers,
  FaGem,
  FaTrophy,
  FaCameraRetro,
  FaMapMarkedAlt,
  FaCompass,
} from 'react-icons/fa';

// Create a badge icon mapping object
const BADGE_ICONS = {
  profile_starter: {
    icon: <FaUserCheck />,
    color: 'linear-gradient(135deg, #FF6B6B, #FFE66D)',
    border: '#FFE66D',
    shadow: 'rgba(255, 107, 107, 0.4)',
  },
  profile_pro: {
    icon: <FaCameraRetro />,
    color: 'linear-gradient(135deg, #00F5A0, #00D9F5)',
    border: '#00F5A0',
    shadow: 'rgba(0, 245, 160, 0.4)',
  },
  profile_master: {
    icon: <FaGem />,
    color: 'linear-gradient(135deg, #7F00FF, #E100FF)',
    border: '#E100FF',
    shadow: 'rgba(127, 0, 255, 0.4)',
  },
  global_starter: {
    icon: <FaMapMarkedAlt />,
    color: 'linear-gradient(135deg, #0061ff, #60efff)',
    border: '#60efff',
    shadow: 'rgba(0, 97, 255, 0.4)',
  },
  global_explorer: {
    icon: <FaCompass />,
    color: 'linear-gradient(135deg, #1CB5E0, #000851)',
    border: '#1CB5E0',
    shadow: 'rgba(28, 181, 224, 0.4)',
  },
  global_master: {
    icon: <FaGlobe />,
    color: 'linear-gradient(135deg, #000428, #004e92)',
    border: '#004e92',
    shadow: 'rgba(0, 78, 146, 0.4)',
  },
  service_starter: {
    icon: <FaMedal />,
    color: 'linear-gradient(135deg, #F9D423, #FF4E50)',
    border: '#F9D423',
    shadow: 'rgba(249, 212, 35, 0.4)',
  },
  service_pro: {
    icon: <FaAward />,
    color: 'linear-gradient(135deg, #FF512F, #F09819)',
    border: '#F09819',
    shadow: 'rgba(255, 81, 47, 0.4)',
  },
  service_master: {
    icon: <FaCrown />,
    color: 'linear-gradient(135deg, #FFD700, #FFA500)',
    border: '#FFD700',
    shadow: 'rgba(255, 215, 0, 0.4)',
  },
  loved_starter: {
    icon: <FaHeart />,
    color: 'linear-gradient(135deg, #FF0844, #FFB199)',
    border: '#FFB199',
    shadow: 'rgba(255, 8, 68, 0.4)',
  },
  loved_pro: {
    icon: <FaStar />,
    color: 'linear-gradient(135deg, #FF0099, #FF6A00)',
    border: '#FF6A00',
    shadow: 'rgba(255, 0, 153, 0.4)',
  },
  loved_master: {
    icon: <FaTrophy />,
    color: 'linear-gradient(135deg, #FFE259, #FFA751)',
    border: '#FFE259',
    shadow: 'rgba(255, 226, 89, 0.4)',
  },
  invitation_starter: {
    icon: <FaHandshake />,
    color: 'linear-gradient(135deg, #00C6FB, #005BEA)',
    border: '#00C6FB',
    shadow: 'rgba(0, 198, 251, 0.4)',
  },
  invitation_pro: {
    icon: <FaUsers />,
    color: 'linear-gradient(135deg, #3C8CE7, #00EAFF)',
    border: '#00EAFF',
    shadow: 'rgba(60, 140, 231, 0.4)',
  },
  invitation_master: {
    icon: <FaCrown />,
    color: 'linear-gradient(135deg, #FFAA00, #FBDA61)',
    border: '#FBDA61',
    shadow: 'rgba(255, 170, 0, 0.4)',
  },
};

function BadgeManagement() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  const filteredBadges = Object.values(BADGES).filter((badge) => {
    const matchesCategory =
      selectedCategory === 'all' || badge.category === selectedCategory;
    const matchesSearch =
      badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      badge.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const badgeStats = {
    total: Object.keys(BADGES).length,
    byCategory: Object.values(BADGES).reduce((acc, badge) => {
      acc[badge.category] = (acc[badge.category] || 0) + 1;
      return acc;
    }, {}),
  };

  return (
    <div className="badge-management">
      <div className="badge-management-header">
        <div className="badge-stats">
          <h2>Badge Management</h2>
          <div className="stats-pills">
            <span className="stat-pill">Total Badges: {badgeStats.total}</span>
            <span className="stat-pill">
              Categories: {Object.keys(badgeStats.byCategory).length}
            </span>
          </div>
        </div>
        <button className="create-badge-btn">
          <FaPlus /> New Badge
        </button>
      </div>

      <div className="badge-controls">
        <div className="search-filter">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search badges..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="badge-search"
            />
          </div>
          <div className="filter-wrapper">
            <FaFilter className="filter-icon" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-filter"
            >
              <option value="all">All Categories</option>
              {Object.values(BADGE_CATEGORIES).map((category) => (
                <option key={category} value={category}>
                  {category} ({badgeStats.byCategory[category] || 0})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      <div className={`badges-container ${viewMode}`}>
        {filteredBadges.map((badge) => (
          <div key={badge.id} className="badge-card">
            <div className="badge-header">
              <div
                className="badge-icon-wrapper"
                style={{
                  background:
                    BADGE_ICONS[badge.id]?.color ||
                    'linear-gradient(45deg, #00f0ff, #0066ff)',
                  borderColor: BADGE_ICONS[badge.id]?.border,
                  boxShadow: `0 8px 24px ${BADGE_ICONS[badge.id]?.shadow}`,
                }}
              >
                {BADGE_ICONS[badge.id]?.icon || <FaAward />}
                <div className="badge-shine"></div>
              </div>
              <div className="badge-title">
                <h3>{badge.name}</h3>
                <span className="badge-category">{badge.category}</span>
              </div>
            </div>

            <div className="badge-content">
              <p className="badge-description">{badge.description}</p>
            </div>

            <div className="badge-actions">
              <button className="btn-edit">
                <FaEdit /> Edit
              </button>
              <button className="btn-delete">
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredBadges.length === 0 && (
        <div className="no-results">
          <p>No badges found matching your criteria</p>
        </div>
      )}
    </div>
  );
}

export default BadgeManagement;
